// src/components/dashboard/DailyIntelligenceCard.jsx
// Daily Intelligence Card — three personalized data points shown on Dashboard.
// Portfolio pulse · Biggest risk signal · One action prompt.
// Data: portfolio_snapshots (pulse delta) + risk_scores (sub-component max).

import { useState, useEffect } from 'react'
import { supabase, hasSupabase } from '../../lib/supabaseClient'
import { getOrCalculateRiskScore } from '../../lib/riskScore'

// ── Constants ────────────────────────────────────────────────────────────────

const RISK_LABELS = {
  sector_risk:     'SECTOR CONCENTRATION',
  geo_risk:        'GEO-POLITICAL RISK',
  volatility_risk: 'ASSET VOLATILITY',
  currency_risk:   'CURRENCY EXPOSURE',
  balance_risk:    'PORTFOLIO BALANCE',
}

const ACTION_PROMPTS = {
  sector_risk:     'Your portfolio has high sector concentration. Consider diversifying.',
  geo_risk:        'Geopolitical risk detected. Review your regional exposure.',
  volatility_risk: 'High volatility detected. Check your position sizes.',
  currency_risk:   'Currency exposure is elevated. Monitor FX movements.',
  balance_risk:    'Portfolio imbalance detected. Consider rebalancing.',
}

function riskColor(score) {
  if (score >= 70) return 'var(--negative)'
  if (score >= 40) return 'var(--caution)'
  return 'var(--positive)'
}

function fmtDollar(val) {
  if (val == null) return '—'
  const abs = Math.abs(val)
  const sign = val >= 0 ? '+' : '-'
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`
  if (abs >= 1_000)     return `${sign}$${(abs / 1_000).toFixed(1)}K`
  return `${sign}$${abs.toFixed(0)}`
}

// ── Sub-components ───────────────────────────────────────────────────────────

function Divider() {
  return (
    <div style={{
      width: 1,
      alignSelf: 'stretch',
      background: 'var(--border)',
      margin: '0 4px',
      flexShrink: 0,
    }} />
  )
}

function Panel({ label, children }) {
  return (
    <div style={{ flex: 1, minWidth: 0, padding: '0 20px' }}>
      <div style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 9,
        color: 'var(--text-tertiary)',
        letterSpacing: '.14em',
        textTransform: 'uppercase',
        marginBottom: 10,
      }}>
        {label}
      </div>
      {children}
    </div>
  )
}

function Skeleton() {
  return (
    <div style={{
      height: 14,
      width: '70%',
      borderRadius: 4,
      background: 'linear-gradient(90deg, #1a2332 25%, #252f3e 50%, #1a2332 75%)',
      backgroundSize: '200% 100%',
      animation: 'dic-shimmer 1.4s infinite',
    }} />
  )
}

// ── Main component ───────────────────────────────────────────────────────────

export default function DailyIntelligenceCard({ userId, holdings, portfolioReady }) {
  const [pulse, setPulse]     = useState(null)   // { value, delta, deltaPct }
  const [riskData, setRisk]   = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!portfolioReady || !userId) {
      setLoading(false)
      return
    }

    let mounted = true

    async function load() {
      try {
        const [snapRes, risk] = await Promise.all([
          hasSupabase
            ? supabase
                .from('portfolio_snapshots')
                .select('value, snapshot_at')
                .eq('user_id', userId)
                .order('snapshot_at', { ascending: false })
                .limit(2)
            : Promise.resolve({ data: [] }),
          getOrCalculateRiskScore(userId, holdings ?? []),
        ])

        if (!mounted) return

        const snaps = snapRes.data ?? []
        if (snaps.length >= 2) {
          const curr  = snaps[0].value
          const prev  = snaps[1].value
          const delta = curr - prev
          setPulse({ value: curr, delta, deltaPct: prev > 0 ? (delta / prev) * 100 : 0 })
        } else if (snaps.length === 1) {
          setPulse({ value: snaps[0].value, delta: null, deltaPct: null })
        }

        setRisk(risk)
      } finally {
        if (mounted) setLoading(false)
      }
    }

    load()
    return () => { mounted = false }
  }, [userId, portfolioReady])

  // ── Derive biggest risk ─────────────────────────────────────────────────────
  const biggestRisk = riskData
    ? Object.entries({
        geo_risk:        riskData.geo_risk        ?? 0,
        sector_risk:     riskData.sector_risk     ?? 0,
        volatility_risk: riskData.volatility_risk ?? 0,
        currency_risk:   riskData.currency_risk   ?? 0,
        balance_risk:    riskData.balance_risk    ?? 0,
      }).sort((a, b) => b[1] - a[1])[0]   // [key, score]
    : null

  const pulseUp = pulse?.delta != null ? pulse.delta >= 0 : null

  // ── Empty state ─────────────────────────────────────────────────────────────
  if (!portfolioReady) {
    return (
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderTop: '2px solid var(--accent)',
        borderRadius: 'var(--radius-lg)',
        padding: '18px 24px',
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        <span style={{ fontSize: 16, opacity: .5 }}>◎</span>
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--text-tertiary)',
          letterSpacing: '.04em',
        }}>
          Add holdings to see your risk signal
        </span>
      </div>
    )
  }

  return (
    <>
      <style>{`
        @keyframes dic-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes dic-in {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderTop: '2px solid var(--accent)',
        borderRadius: 'var(--radius-lg)',
        padding: '18px 0',
        marginBottom: 16,
        display: 'flex',
        alignItems: 'stretch',
        animation: 'dic-in .3s ease',
        overflow: 'hidden',
      }}>

        {/* ── Panel 1: Portfolio Pulse ─────────────────────────────────────── */}
        <Panel label="Portfolio Pulse">
          {loading ? (
            <>
              <Skeleton />
              <div style={{ marginTop: 8 }}><Skeleton /></div>
            </>
          ) : pulse ? (
            <>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 22,
                fontWeight: 700,
                color: pulseUp === null ? 'var(--text-primary)' : pulseUp ? 'var(--positive)' : 'var(--negative)',
                lineHeight: 1,
                marginBottom: 6,
              }}>
                {pulse.delta != null
                  ? `${pulse.deltaPct >= 0 ? '+' : ''}${pulse.deltaPct.toFixed(2)}%`
                  : '—'
                }
              </div>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--text-secondary)',
              }}>
                {pulse.delta != null
                  ? `${fmtDollar(pulse.delta)} since last update`
                  : 'No prior snapshot to compare'
                }
              </div>
              {pulse.value != null && (
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 10,
                  color: 'var(--text-tertiary)',
                  marginTop: 4,
                }}>
                  Current value: ${pulse.value.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </div>
              )}
            </>
          ) : (
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--text-tertiary)',
            }}>
              No snapshots yet
            </div>
          )}
        </Panel>

        <Divider />

        {/* ── Panel 2: Biggest Risk Signal ─────────────────────────────────── */}
        <Panel label="Biggest Risk Signal">
          {loading ? (
            <>
              <Skeleton />
              <div style={{ marginTop: 8 }}><Skeleton /></div>
            </>
          ) : biggestRisk ? (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 6,
              }}>
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 22,
                  fontWeight: 700,
                  color: riskColor(biggestRisk[1]),
                  lineHeight: 1,
                }}>
                  {biggestRisk[1]}
                  <span style={{ fontSize: 12, fontWeight: 400 }}>/100</span>
                </div>
                <span style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 9,
                  fontWeight: 700,
                  color: riskColor(biggestRisk[1]),
                  background: `${riskColor(biggestRisk[1])}18`,
                  border: `1px solid ${riskColor(biggestRisk[1])}35`,
                  padding: '2px 7px',
                  borderRadius: 'var(--radius-sm)',
                  letterSpacing: '.08em',
                  alignSelf: 'flex-end',
                  marginBottom: 2,
                }}>
                  {biggestRisk[1] >= 70 ? 'HIGH' : biggestRisk[1] >= 40 ? 'ELEVATED' : 'LOW'}
                </span>
              </div>
              <div style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--text-secondary)',
              }}>
                {RISK_LABELS[biggestRisk[0]] ?? biggestRisk[0]}
              </div>
            </>
          ) : (
            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--text-tertiary)',
            }}>
              No risk data available
            </div>
          )}
        </Panel>

        <Divider />

        {/* ── Panel 3: Action Prompt ───────────────────────────────────────── */}
        <Panel label="Action">
          {loading ? (
            <>
              <Skeleton />
              <div style={{ marginTop: 8 }}><Skeleton /></div>
              <div style={{ marginTop: 8 }}><Skeleton /></div>
            </>
          ) : biggestRisk ? (
            <div style={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              color: 'var(--text-primary)',
              lineHeight: 1.55,
            }}>
              {ACTION_PROMPTS[biggestRisk[0]] ?? 'Review your portfolio for potential risks.'}
            </div>
          ) : (
            <div style={{
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              color: 'var(--text-tertiary)',
              lineHeight: 1.5,
            }}>
              Add holdings to get a personalised recommendation.
            </div>
          )}
        </Panel>

      </div>
    </>
  )
}
